import React from "react";

import { Navigate, Route, Routes } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";

import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";

import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";

import Appbar from "./components/drawer/Appbar";
import AppDrawer from "./components/drawer/AppDrawer";

import Home from "../home/Home";
import About from "../about/About";

import SignIn from "../auth/SignIn";
import SignUp from "../auth/SignUp";
import LogOut from "../auth/LogOut";

import Statistics from "../database/statistics/Statistics.jsx";
import Libraries from "../database/libraries/Libraries";
import Playlists from "../database/playlists/Playlists";
import Resources from "../database/resources/Resources";
import Transcripts from "../database/transcripts/Transcripts";
import TranscriptView from "../database/transcripts/TranscriptView";
import TranscriptEdit from "../database/transcripts/TranscriptEdit";
import Quotes from "../database/quotes/Quotes";
import Authors from "../database/authors/Authors";

import TextTranslations from "../translations/text/TextTranslations.jsx";
import TextTranslationView from "../translations/text/TextTranslationView.jsx";
import AudioTranslations from "../translations/audio/AudioTranslations.jsx";
import AudioTranslationView from "../translations/audio/AudioTranslationView.jsx";

import HighlightRequests from "../highlights/requests/HighlightRequests.jsx";
import Highlights from "../highlights/review/Highlights.jsx";
import HighlightView from "../highlights/review/HighlightView.jsx";

import Account from "../management/account/Account";
import Users from "../management/users/Users";
import Cookies from "../management/cookies/Cookies";

import NoMatch from "../../helper-components/pages/NoMatch";
import Copyright from "../../helper-components/misc/Copyright";
import UnexpectedError from "../../helper-components/pages/UnexpectedError";

import { queryLibraries } from "../../helper-functions/api/libraryQueries";
import { queryResources } from "../../helper-functions/api/resourceQueries";
import { queryTranscripts } from "../../helper-functions/api/transcriptQueries.js";
import { getPathFromUrl, handleTourGuide, scrollToTop } from "../../helper-functions/shared/pageUtils";
import { executeFunctionsAfterConnectionEstablished, executeFunctionsAfterLogin, executeFunctionsAfterPageLoad } from "../../helper-functions/api/apiUtils";

import * as Constants from "../../config/constants/Constants.js";

/**
 * Main page, which includes:
 * sidebars
 * toolbar
 * routes to all sub-pages, which will be shown in the center of the page.
 *
 * Add any periodic queries or functions executed after page reload here.
 */
export default function MainPage(props) {
    const { runTourGuide, setRunTourGuide, tourGuideSteps, setTourGuideSteps, tourGuideStepIndex, setTourGuideStepIndex } = props.browser;

    const { libraries, resources, transcripts, quotes } = props.backend;

    const path = getPathFromUrl();

    // Temporary state to keep track of already queried ids.
    const [alreadyQueriedResourceIds, setAlreadyQueriedResourceIds] = React.useState([null]);
    const [alreadyQueriedTranscriptIds, setAlreadyQueriedTranscriptIds] = React.useState([null]);
    const [alreadyQueriedLibraryIds, setAlreadyQueriedLibraryIds] = React.useState([null]);

    // useEffect gets automatically executed when page is loaded.
    React.useEffect(() => {
        console.log("Page reloaded.");
        executeFunctionsAfterPageLoad(props);
    }, []);

    React.useEffect(() => {
        if (props.server.isConnectionToServerEstablished) {
            console.log("Connection with server established.");
            executeFunctionsAfterConnectionEstablished(props);
        }
    }, [props.server.isConnectionToServerEstablished]);

    React.useEffect(() => {
        if (!props.user.isLoggedIn) return;

        console.log("User logged in.");
        handleTourGuide(props);
        executeFunctionsAfterLogin(props);
    }, [props.user.isLoggedIn]);

    // If over N days have passed since the last cookie was created, show a dialog to the user.
    React.useEffect(() => {
        const user = props.user.me;

        if (!user || user?.role?.role !== "ADMIN") return;
        if (props.browser.showInitialTourGuide) return;

        if (props.backend.programStatus?.require_youtube_cookies) {
            const youtubeCookie = props.backend.recentCookies.youtube;

            if (youtubeCookie) {
                const createdOn = new Date(youtubeCookie.created_on);
                const currentDate = new Date();
                const timeDiff = currentDate.getTime() - createdOn.getTime();
                const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24)); // Difference in days.

                const days = props.backend.programStatus?.remind_youtube_cookies_after_days;

                if (diffDays >= days) {
                    props.showDialog({
                        title: `Cookie expiration reminder`,
                        message: `It's been ${diffDays} days since the last YOUTUBE.COM cookie was created.

                        Due to YouTube security features, a new cookie might be required soon.
                        Please create (or direct the developer to create) a new cookie to ensure the platform continues to work properly.`,
                        actionTitle: "Understood",
                        cancelTitle: ""
                    });
                }
            }
        }

        if (props.backend.programStatus?.require_vimeo_cookies) {
            const vimeoCookie = props.backend.recentCookies.vimeo;

            if (vimeoCookie) {
                const createdOn = new Date(vimeoCookie.created_on);
                const currentDate = new Date();
                const timeDiff = currentDate.getTime() - createdOn.getTime();
                const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24)); // Difference in days.

                const days = props.backend.programStatus?.remind_vimeo_cookies_after_days;

                if (diffDays >= days) {
                    props.showDialog({
                        title: `Cookie expiration reminder`,
                        message: `It's been ${diffDays} days since the last VIMEO.COM cookie was created.

                        Due to Vimeo security features, a new cookie might be required soon.
                        Please create (or direct the developer to create) a new cookie to ensure the platform continues to work properly.`,
                        actionTitle: "Understood",
                        cancelTitle: ""
                    });
                }
            }
        }
    }, [props.backend.recentCookies.youtube, props.backend.recentCookies.vimeo, props.backend.programStatus, props.browser.showInitialTourGuide]);

    // Scroll to the top of the page when the route changes.
    React.useEffect(() => {
        scrollToTop();
    }, [props.browser.location.pathname]);

    React.useEffect(() => {
        handleTourGuideSteps();
    }, [path]);

    // If a transcript references an unknown resource, query it.
    React.useEffect(() => {
        if (!resources?.data || !transcripts?.data || transcripts?.data?.length <= 0) return;

        const ids = transcripts?.data.map((t) => t.resource_id);
        const unknownIds = [...new Set(ids.filter((id) => !alreadyQueriedResourceIds.includes(id) && !resources.data.find((r) => r.id === id)))];

        if (unknownIds.length > 0) {
            console.log("Found unknown resources, querying: ", unknownIds);
            setAlreadyQueriedResourceIds((prev) => [...prev, ...unknownIds]);

            queryResources(props, {
                filter: {
                    items: [
                        {
                            columnField: "id",
                            operatorValue: "isAnyOf",
                            value: unknownIds
                        }
                    ]
                },
                pageSize: unknownIds.length,
                pagination: false
            });
        }
    }, [transcripts]);

    // If a resource references an unknown library, query it.
    React.useEffect(() => {
        if (!resources?.data || !libraries?.data || resources?.data?.length <= 0) return;

        const ids = resources?.data.map((r) => r.library_id);
        const unknownIds = [...new Set(ids.filter((id) => !alreadyQueriedLibraryIds.includes(id) && !libraries.data.find((t) => t.id === id)))];

        if (unknownIds.length > 0) {
            console.log("Found unknown libraries, querying: ", unknownIds);
            setAlreadyQueriedLibraryIds((prev) => [...prev, ...unknownIds]);

            queryLibraries(props, {
                filter: {
                    items: [
                        {
                            columnField: "id",
                            operatorValue: "isAnyOf",
                            value: unknownIds
                        }
                    ]
                },
                pageSize: unknownIds.length,
                pagination: false
            });
        }
    }, [resources]);

    // If a resource references an unknown transcript, query it.
    React.useEffect(() => {
        if (!resources?.data || !transcripts?.data || resources?.data?.length <= 0) return;

        const ids = resources?.data.map((r) => r.id);
        const unknownIds = [...new Set(ids.filter((id) => !alreadyQueriedTranscriptIds.includes(id) && !transcripts.data.find((t) => t.resource_id === id)))];

        if (unknownIds.length > 0) {
            console.log("Found unknown transcripts, querying: ", unknownIds);
            setAlreadyQueriedTranscriptIds((prev) => [...prev, ...unknownIds]);

            queryTranscripts(props, {
                filter: {
                    items: [
                        {
                            columnField: "resource_id",
                            operatorValue: "isAnyOf",
                            value: unknownIds
                        }
                    ]
                },
                pageSize: unknownIds.length,
                pagination: false
            });
        }
    }, [resources]);

    // If a quote references an unknown transcript, query it.
    React.useEffect(() => {
        if (!quotes?.data || !transcripts?.data || quotes?.data?.length <= 0) return;

        const ids = quotes?.data.map((r) => r.transcript_id);
        const unknownIds = [...new Set(ids.filter((id) => !alreadyQueriedTranscriptIds.includes(id) && !transcripts.data.find((t) => t.id === id)))];

        if (unknownIds.length > 0) {
            console.log("Found unknown quotes, querying: ", unknownIds);
            setAlreadyQueriedTranscriptIds((prev) => [...prev, ...unknownIds]);

            queryTranscripts(props, {
                filter: {
                    items: [
                        {
                            columnField: "id",
                            operatorValue: "isAnyOf",
                            value: unknownIds
                        }
                    ]
                },
                pageSize: unknownIds.length,
                pagination: false
            });
        }
    }, [quotes]);

    /**
     * Application tour guide.
     */
    const handleJoyrideCallback = (data) => {
        const { action, index, status, type } = data;

        // If the tour is finished or skipped, set the state to not run the tour.
        if ([STATUS.FINISHED, STATUS.SKIPPED, STATUS.PAUSED].includes(status) || [ACTIONS.CLOSE].includes(action)) {
            setRunTourGuide(false);
            setTourGuideStepIndex(0);
        }

        // Update state to advance the tour.
        else if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
            setTourGuideStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
        }
    };

    /**
     * Set the tour guide steps according to the current path.
     * If the tour is already running, cancel it.
     */
    const handleTourGuideSteps = () => {
        let steps = [];

        Object.values(Constants.TOUR_GUIDE_STEPS).forEach((guide) => {
            if (guide.paths.includes(path)) {
                steps = guide.steps;
            }
        });

        // Update steps if they are different from the current ones.
        if (steps[0]?.content !== tourGuideSteps[0]?.content) {
            // Cancel the tour if it's already running.
            if (runTourGuide) {
                setRunTourGuide(false);
            }

            setTourGuideSteps(steps);
            setTourGuideStepIndex(0);
        }
    };

    return (
        // This component includes the toolbar, drawer and other components that remain the same everywhere you are.
        <Box sx={{ display: "flex", minHeight: "100vh" }}>
            <Joyride
                run={runTourGuide}
                steps={tourGuideSteps}
                stepIndex={tourGuideStepIndex}
                styles={{
                    options: {
                        primaryColor: "#9c27b0",
                        zIndex: 9999
                    }
                }}
                locale={{ last: "Close tour" }}
                continuous
                showProgress
                // showSkipButton
                spotlightClicks
                scrollToFirstStep
                disableOverlayClose
                disableScrollParentFix //NB! This needs to be here to fix tables stretching the page.
                callback={handleJoyrideCallback}
            />

            {props.browser.showMainPageComponents && (
                <>
                    <Appbar {...props} />
                    <AppDrawer {...props} />
                </>
            )}

            <Box
                component="main"
                sx={{
                    backgroundColor: (theme) => (theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[900]),
                    flexGrow: 1,
                    overflow: "auto"
                }}>
                {props.browser.showMainPageComponents && <Toolbar />}

                <ErrorBoundary
                    FallbackComponent={({ error, resetErrorBoundary }) => {
                        return <UnexpectedError error={error} resetErrorBoundary={resetErrorBoundary} />;
                    }}>
                    {/* These components here are the only things that will change when the user clicks on another tab. */}
                    <Routes>
                        {/* Home */}
                        <Route path="/" element={<Navigate replace to="/home" />} />
                        <Route path="/home" element={<Home {...props} />} />

                        <Route path="/about" element={<About {...props} />} />
                        <Route path="/admin/about" element={<About {...props} />} />

                        {/* Database tables */}
                        <Route path="/database/statistics" element={<Statistics {...props} />} />

                        <Route path="/database/libraries" element={<Libraries {...props} />} />

                        <Route path="/database/playlists" element={<Playlists {...props} />} />

                        <Route path="/database/resources" element={<Resources {...props} />} />

                        <Route path="/database/transcripts" element={<Transcripts {...props} />} />
                        <Route path="/database/transcripts/:id/view" element={<TranscriptView {...props} />} />
                        <Route path="/database/transcripts/:id/edit" element={<TranscriptEdit {...props} />} />

                        <Route path="/database/quotes" element={<Quotes {...props} />} />

                        <Route path="/database/authors" element={<Authors {...props} />} />

                        <Route path="/highlights/requests" element={<HighlightRequests {...props} />} />
                        <Route path="/highlights/view" element={<Highlights {...props} />} />
                        <Route path="/highlights/view/:id" element={<HighlightView {...props} />} />

                        <Route path="/translations/text" element={<TextTranslations {...props} />} />
                        <Route path="/translations/text/:id" element={<TextTranslationView {...props} />} />
                        <Route path="/translations/audio" element={<AudioTranslations {...props} />} />
                        <Route path="/translations/audio/:id" element={<AudioTranslationView {...props} />} />

                        {/* Auth */}
                        <Route path="/signin" element={<SignIn {...props} />} />
                        <Route path="/signup" element={<SignUp {...props} />} />
                        <Route path="/logout" element={<LogOut {...props} />} />

                        {/* Admin */}
                        <Route path="/admin/account" element={<Account {...props} />} />
                        <Route path="/admin/users" element={<Users {...props} />} />
                        <Route path="/admin/cookies" element={<Cookies {...props} />} />

                        {/* Misc. */}
                        <Route path="/error" element={<UnexpectedError {...props} />} />
                        <Route path="*" element={<NoMatch />} />
                    </Routes>
                </ErrorBoundary>

                <Copyright />
            </Box>
        </Box>
    );
}
